import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Stack, Chip, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Autocomplete, Tooltip, MenuItem,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { PRIMARY, PRIMARY_LIGHT } from '../../theme';
import { solidBtn, ghostBtn, field, th, td, EmptyState } from '../uiKit';
import useOrgOptions from '../../hooks/useOrgOptions';
import { readPolicy, writePolicy } from '../../utils/permissionStore';

const PERIODS = [{ key: 'month', label: 'Month' }, { key: 'year', label: 'Year' }];

// HR gives permission hours per Month or per Year. Pick departments (none picked = all departments), add the
// rule, and remove any rule that is no longer wanted.
export default function PermissionSettings({ open, onClose, entityId, onSaved }) {
    const { departmentNames: departments } = useOrgOptions();
    const [rules, setRules] = useState([]);
    const [hours, setHours] = useState('');
    const [period, setPeriod] = useState('month');
    const [depts, setDepts] = useState([]);
    const [tried, setTried] = useState(false);

    useEffect(() => {
        if (!open) return;
        setRules(readPolicy(entityId).rules);
        setHours(''); setPeriod('month'); setDepts([]); setTried(false);
    }, [open, entityId]);

    const hoursOk = hours !== '' && Number(hours) > 0;
    const add = () => {
        setTried(true);
        if (!hoursOk) return;
        // A department can have one rule per period: the new rule takes those departments over from older ones.
        const next = rules.flatMap((r) => {
            const named = r.departments || [];
            if (r.period !== period) return [r];
            if (!depts.length) return named.length ? [r] : []; // a new all-departments rule replaces the old one
            if (!named.length) return [r]; // the all-departments rule stays as the fallback
            const left = named.filter((d) => !depts.includes(d));
            return left.length ? [{ ...r, departments: left }] : [];
        });
        setRules([...next, { id: `rule-${Date.now()}`, hours: Number(hours), period, departments: depts }]);
        setHours(''); setDepts([]); setTried(false);
    };
    const remove = (id) => setRules((list) => list.filter((r) => r.id !== id));

    const save = () => { onSaved(writePolicy(entityId, { rules })); onClose(); };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.2, pb: 1 }}>
                <TuneRoundedIcon sx={{ color: PRIMARY }} />
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>Permission Settings</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280' }}>How many permission hours one person can take in a month or a year.</Typography>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseRoundedIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ bgcolor: '#fff' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '130px 140px minmax(240px, 1fr) auto' }, gap: 1.5, alignItems: 'start' }}>
                    <TextField label="Hours" size="small" type="number" value={hours} onChange={(e) => setHours(e.target.value)}
                        error={tried && !hoursOk} helperText={tried && !hoursOk ? 'Enter hours' : undefined}
                        slotProps={{ htmlInput: { min: 0, step: 0.5 }, inputLabel: { shrink: true } }} sx={field} />
                    <TextField select label="Per" size="small" value={period} onChange={(e) => setPeriod(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }} sx={field}>
                        {PERIODS.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
                    </TextField>
                    <Autocomplete multiple size="small" disableCloseOnSelect limitTags={2} options={departments} value={depts}
                        onChange={(_, v) => setDepts(v)} noOptionsText="No departments"
                        renderInput={(params) => <TextField {...params} label="Departments" placeholder={depts.length ? '' : 'All departments'} sx={field}
                            slotProps={{ ...params.slotProps, inputLabel: { shrink: true } }} />} />
                    <Button onClick={add} sx={{ ...solidBtn, height: 40, px: 3 }}>Add</Button>
                </Box>
                <Typography sx={{ fontSize: 12, color: '#98A0AE', mt: 0.8 }}>Leave Departments empty to give the same hours to every department.</Typography>

                <Box sx={{ mt: 2.5, border: '1px solid #EEF0F6', borderRadius: '9px', overflow: 'hidden' }}>
                    {rules.length === 0 ? (
                        <EmptyState icon={TuneRoundedIcon} title="No permission hours set" hint="Add hours above. With no rule, nobody has a limit." />
                    ) : (
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                            <Box component="thead" sx={{ bgcolor: '#F4F3FB' }}>
                                <Box component="tr">
                                    {['HOURS', 'PER', 'DEPARTMENTS', ''].map((h) => <Box component="th" key={h || 'x'} sx={{ ...th, px: 2 }}>{h}</Box>)}
                                </Box>
                            </Box>
                            <Box component="tbody">
                                {rules.map((r) => (
                                    <Box component="tr" key={r.id}>
                                        <Box component="td" sx={{ ...td, px: 2, fontSize: 14, fontWeight: 800, color: '#0F172A' }}>{r.hours} hr</Box>
                                        <Box component="td" sx={{ ...td, px: 2, fontSize: 13, color: '#334155' }}>{r.period === 'year' ? 'Year' : 'Month'}</Box>
                                        <Box component="td" sx={{ ...td, px: 2 }}>
                                            {(r.departments || []).length === 0
                                                ? <Chip size="small" label="All departments" sx={{ fontWeight: 700, bgcolor: '#DCFCE7', color: '#16A34A' }} />
                                                : <Stack direction="row" sx={{ gap: 0.6, flexWrap: 'wrap' }}>{r.departments.map((d) => <Chip key={d} size="small" label={d} sx={{ fontWeight: 600, bgcolor: PRIMARY_LIGHT, color: PRIMARY }} />)}</Stack>}
                                        </Box>
                                        <Box component="td" sx={{ ...td, px: 2, textAlign: 'right' }}>
                                            <Tooltip arrow title="Remove"><IconButton size="small" onClick={() => remove(r.id)}><DeleteOutlineRoundedIcon sx={{ fontSize: 19, color: '#DC2626' }} /></IconButton></Tooltip>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.8 }}>
                <Button onClick={onClose} sx={{ ...ghostBtn, height: 40, px: 3 }}>Cancel</Button>
                <Button onClick={save} sx={{ ...solidBtn, height: 40, px: 4 }}>Save settings</Button>
            </DialogActions>
        </Dialog>
    );
}
